import React, { useId, useState } from 'react';
import { Alert, Card, CardBody, CardHeader, CardText, Collapse, FormGroup, Input, Label } from 'reactstrap';

import { Button } from '~components';
import Icon, { faChevronDown, faChevronUp, faQuestionCircle, faThumbsDown, faThumbsUp } from '~components/icon';

import { useCreateUserFeedbackMutation } from '~main/actions/userFeedback';

export interface ComponentProps {
  workflowType: string;
  isVisible?: boolean;
  className?: string;
  onSubmitSuccess?: () => void;
}

const UserFeedback: React.FC<ComponentProps> = ({
  workflowType,
  isVisible = false,
  className = '',
  onSubmitSuccess,
}) => {
  const commentId = useId();
  const [rating, setRating] = useState<'positive' | 'negative' | null>(null);
  const [comment, setComment] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [createUserFeedback, { isLoading, isError }] = useCreateUserFeedbackMutation();

  const handleSubmit = async () => {
    if (!rating) return;

    try {
      await createUserFeedback({
        workflow_type: workflowType,
        rating,
        comment: comment.trim() || undefined,
      }).unwrap();

      setIsSubmitted(true);

      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      console.warn('Failed to submit feedback:', err);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className={`user-feedback ${className}`}>
      <CardHeader
        tag="button"
        type="button"
        className="d-flex justify-content-between border-0 fw-bold align-items-center"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div>
          <Icon icon={faQuestionCircle} className="text-success me-2" />
          Got feedback for us?
        </div>
        <Icon icon={isOpen ? faChevronUp : faChevronDown} className="ms-2" />
      </CardHeader>
      <Collapse isOpen={isOpen}>
        {!isSubmitted ? (
          <CardBody>
            <div className="d-flex gap-4 mb-2 justify-content-center">
              <Button
                color={rating === 'positive' ? 'success' : 'outline-success'}
                size="lg"
                onClick={() => setRating('positive')}
                disabled={isLoading}
                className="d-flex align-items-center gap-2"
                aria-label="Positive feedback"
              >
                <Icon icon={faThumbsUp} size="2xl" />
              </Button>
              <Button
                color={rating === 'negative' ? 'danger' : 'outline-danger'}
                size="lg"
                onClick={() => setRating('negative')}
                disabled={isLoading}
                className="d-flex align-items-center gap-2"
                aria-label="Negative feedback"
              >
                <Icon icon={faThumbsDown} size="2xl" />
              </Button>
            </div>

            {rating === null ? (
              <CardText className="text-center text-muted mb-0">Your feedback helps us improve</CardText>
            ) : (
              <div>
                <FormGroup>
                  <Label for={commentId} className="text-muted">
                    Tell us more (optional)
                  </Label>
                  <Input
                    id={commentId}
                    type="textarea"
                    rows={3}
                    placeholder="Share your thoughts, suggestions, or feature requests..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={isLoading}
                  />
                </FormGroup>

                <div className="d-flex gap-2 justify-content-end">
                  <Button color="success" onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </div>
            )}

            {isError && (
              <Alert color="danger" className="mt-3 mb-0 small">
                Failed to submit feedback. Please try again.
              </Alert>
            )}
          </CardBody>
        ) : (
          <CardBody className="text-center">
            <Alert color="success" className="mb-0">
              <Icon icon={faThumbsUp} className="me-2" />
              Thank you for your feedback!
            </Alert>
          </CardBody>
        )}
      </Collapse>
    </Card>
  );
};

export default UserFeedback;
